import React, { useState } from 'react';
import { View, Text, TextInput, Pressable, StyleSheet } from 'react-native';
import { Trans } from '@lingui/react/macro';
import {
    type LocalizedMessage,
    useOptionalResolvedMessage,
    useResolvedMessage,
} from '../../lib/i18n/messages';

interface ChipInputProps {
    label: LocalizedMessage;
    value: string[];
    onChange: (value: string[]) => void;
    placeholder?: LocalizedMessage;
    tip?: LocalizedMessage;
    tipWarning?: boolean;
    disabled?: boolean;
}

export default function ChipInput({
    label,
    value,
    onChange,
    placeholder,
    tip,
    tipWarning = false,
    disabled = false,
}: ChipInputProps) {
    const [text, setText] = useState('');
    const resolvedLabel = useResolvedMessage(label);
    const resolvedPlaceholder = useOptionalResolvedMessage(placeholder);
    const resolvedTip = useOptionalResolvedMessage(tip);
    
    const addChip = (t: string) => {
        const parts = t.split(',').map((x) => x.trim()).filter(Boolean);
        if (parts.length === 0) return;
        const next = [...value];
        for (const p of parts) if (!next.includes(p)) next.push(p);
        onChange(next);
        setText('');
    };

    const handleTextChange = (newText: string) => {
        if (newText.includes(',')) {
            addChip(newText);
        } else {
            setText(newText);
        }
    };
    
    return (
        <View>
            <Text style={styles.label}>{resolvedLabel}</Text>
            {tip && (
                <Text style={[styles.tip, tipWarning && styles.tipWarn].filter(Boolean)}>
                    {resolvedTip}
                </Text>
            )}
            {value.length > 0 && (
                <View style={styles.chipsRow}>
                    {value.map((v) => (
                        <Pressable
                            key={v}
                            style={({ pressed }) => [
                                styles.chipSm,
                                pressed && !disabled && styles.chipPressed
                            ].filter(Boolean)}
                            onPress={() => !disabled && onChange(value.filter((x) => x !== v))}
                            disabled={disabled}
                        >
                            <Text style={styles.chipText}>{v} ×</Text>
                        </Pressable>
                    ))}
                </View>
            )}
            <TextInput
                style={styles.inputField}
                placeholder={value.length === 1 ? undefined : resolvedPlaceholder ?? undefined}
                placeholderTextColor="#9ca3af"
                value={text}
                onChangeText={handleTextChange}
                onSubmitEditing={() => addChip(text)}
                onBlur={() => addChip(text)}
                returnKeyType="done"
                editable={!disabled}
            />
            <Text style={styles.smallMuted}><Trans id="chipInput.addAndRemoveHint" comment="Instructions for adding and removing keyword chips">Press return or comma to add; tap a chip to remove.</Trans></Text>
        </View>
    );
}

const styles = StyleSheet.create({
    label: {
        fontSize: 14,
        color: "#334155",
        marginBottom: 6,
        fontWeight: "600",
    },
    tip: {
        fontSize: 12,
        color: "#64748b",
        marginTop: 6,
    },
    tipWarn: {
        color: '#dc2626'
    },
    inputField: {
        backgroundColor: "#ffffff",
        borderColor: "#cbd5e1",
        borderWidth: 1,
        borderRadius: 10,
        paddingVertical: 12,
        paddingHorizontal: 12,
        fontSize: 16,
        color: "#0f172a",
    },
    chipsRow: {
        flexDirection: "row",
        flexWrap: "wrap",
        marginTop: 10,
        marginBottom: 8,
    },
    chipSm: {
        paddingVertical: 8,
        paddingHorizontal: 12,
        borderRadius: 999,
        backgroundColor: "#f8fafc",
        borderWidth: 1,
        borderColor: "#e2e8f0",
        marginRight: 8,
        marginBottom: 8,
    },
    chipPressed: {
        backgroundColor: "#f1f5f9",
    },
    chipText: {
        fontSize: 12,
        color: "#334155",
    },
    smallMuted: {
        fontSize: 12,
        color: "#64748b",
        marginTop: 6,
    },
});
